import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { BillOut, CloseBillFiscalInvoiceIn } from "../../../../../api/bills";
import type { BillHistory } from "../../../../../api/reportsBillsHistory";
import type { Invoice } from "../../../../../api/invoices";
import { useDefaultCompany } from "../../../../../hooks/useCompanies";
import { useIsEdgeLocal } from "../../../../../hooks/useEdgeMode";
import { useFiscalInvoiceAvailability } from "../../../../../hooks/useInvoices";
import {
  fiscalInvoiceBillBlockOf,
  fiscalInvoiceCardTerminalOf,
  invoiceIssuingOf,
  localizedTextOf,
  softwareInvoicesAllowedIn,
  usesSharedInvoiceSeries,
} from "../../../../../lib/fiscalInvoice";
import { getApiErrorMessage } from "../../../../../utils/translations";
import type { DepositInvoiceSource } from "./depositInvoice";
import { FiscalInvoiceBuyerModal } from "./FiscalInvoiceBuyerModal";
import { InvoiceBillSelectionModal } from "./InvoiceBillSelectionModal";
import { InvoiceCreateModal } from "./InvoiceCreateModal";
import { InvoicePreviewModal } from "./InvoicePreviewModal";
import { InvoicingChoiceModal } from "./InvoicingChoiceModal";
import { useOpenInvoicing } from "./useOpenInvoicing";

/**
 * „Фактуриране" от лентата под масите — изваден от `TableFooter.tsx`: изборът
 * (`InvoicingChoiceModal`), сметките, формата и прегледът. Записаната фактура
 * отива направо в прегледа — POS и A4 печатът са там (до 0.0.390 преди него
 * питаше „Печат през POS?“ — същото, втори път). Вторият печат в прегледа е
 * дубликат (`useInvoicePrint`). `openDepositInvoice` отваря формата направо
 * за току-що записан депозит с готов бон („Депозит" → „Нов депозит" с „Фактуриране") — с реда на
 * аванса, без нов бон (`POST /invoices` с `deposit_id`).
 *
 * Входът е винаги изборът, като при „Депозит":
 *  • „Текуща сметка" — само когато се предлага фискална фактура при закриване
 *    (вход D3: подаден `onFiscalInvoiceBuyer` — правото `close_bill`, в
 *    облака, свежата фирма във „Фискално устройство" или „И двете"):
 *    избраната маса → купувачът `FiscalInvoiceBuyerModal` → родителят отваря
 *    СЪЩИЯ „Закрий". Дали апаратът я умее, казва BE
 *    (`GET /invoices/fiscal-availability`, само докато изборът е отворен);
 *  • „Закрита сметка" — софтуерната фактура за закрити сметки
 *    (`InvoiceBillSelectionModal`);
 *  • „Свободна фактура" — същата форма без сметки (само „По сметка", без бон);
 *    само с правото `free_invoice` (`canFreeInvoice`), иначе редът го няма.
 * Двете софтуерни са неактивни с причината във „Фискално устройство".
 */
export function useInvoicingFlow({
  onFiscalInvoiceBuyer,
  selectedBill = null,
  isSelectedBillClosing = false,
  canFreeInvoice = false,
}: {
  onFiscalInvoiceBuyer?: (bill: BillOut, fiscalInvoice: CloseBillFiscalInvoiceIn) => void;
  /** Избраната маса — „Текуща сметка" в избора. */
  selectedBill?: BillOut | null;
  /** Избраната маса се закрива в момента на този терминал. */
  isSelectedBillClosing?: boolean;
  /** Правото „Свободна фактура" (`FREE_INVOICE_PERMISSION`) — редът в избора. */
  canFreeInvoice?: boolean;
} = {}) {
  const { t } = useTranslation();
  const [isSelectionOpen, setIsSelectionOpen] = useState(false);
  // „Текуща сметка" / „Закрита сметка" / „Свободна фактура".
  const [isChoiceOpen, setIsChoiceOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [invoiceBills, setInvoiceBills] = useState<BillHistory[]>([]);
  // „Свободна фактура" — същата форма без сметки (само „По сметка", без бон).
  const [isFreeInvoice, setIsFreeInvoice] = useState(false);
  // Фактура за току-що записан депозит (с готов бон) — с реда на аванса, без
  // нов бон (`POST /invoices` с `deposit_id`).
  const [invoiceDeposit, setInvoiceDeposit] = useState<DepositInvoiceSource | null>(null);
  const [invoicePreview, setInvoicePreview] = useState<Invoice | null>(null);
  // Отворената сметка, за която се въвежда купувачът на фискалната фактура.
  const [fiscalInvoiceBill, setFiscalInvoiceBill] = useState<BillOut | null>(null);
  // „Къде се издават фактурите" на текущата фирма, изтеглена при отварянето
  // (`useOpenInvoicing` пълни кеша; тук само се чете — без втора заявка).
  const { data: defaultCompany } = useDefaultCompany({ enabled: false });
  const invoiceIssuing = invoiceIssuingOf(defaultCompany);
  // Локалният режим не фактурира (`docs/edge.md`) — и BE казва `offline`, но
  // заявката изобщо не тръгва към касата. „Софтуерно" — фискална фактура няма:
  // питането не тръгва и екранът е буквално този отпреди нея.
  const edgeLocal = useIsEdgeLocal();
  const offersFiscalInvoice = (mode: typeof invoiceIssuing) =>
    !!onFiscalInvoiceBuyer && !edgeLocal && usesSharedInvoiceSeries(mode);
  const fiscalInvoiceOffered = offersFiscalInvoice(invoiceIssuing);
  // Отваря се след като `useOpenInvoicing` изтегли СВЕЖАТА фирма — нейният
  // режим (вече в кеша на `useDefaultCompany`) решава редовете.
  const openInvoicing = useOpenInvoicing(() => setIsChoiceOpen(true));
  const availabilityQuery = useFiscalInvoiceAvailability({
    enabled: isChoiceOpen && fiscalInvoiceOffered,
  });
  // Изключеното питане пази в кеша последния отговор — от преди смяна на
  // режима; извън предлагането той не се чете изобщо.
  const fiscalAvailability = fiscalInvoiceOffered ? availabilityQuery.data : undefined;
  // Паднало питане (мрежа, 5xx, 403) — секцията казва защо, вместо да изчезне
  // мълчаливо: във „Фискално устройство“ изборът иначе остава само с
  // „софтуерна няма“, без път напред. Грешката печели и над стар отговор в кеша.
  const fiscalAvailabilityError =
    fiscalInvoiceOffered && availabilityQuery.isError
      ? getApiErrorMessage(availabilityQuery.error, t("fiscalInvoice.picker.loadError"))
      : null;
  // Софтуерната фактура (затворените сметки, „Свободна фактура") — само ако
  // фирмата не е във „Фискално устройство" (BE отказва всяка). Казва го BE на
  // две места: режимът на фирмата и `software_allowed` на питането. Каже ли
  // „не" който и да е — кешираният отговор от предишно отваряне не показва за
  // миг забраненото, докато пристигне новият.
  const softwareAllowed =
    softwareInvoicesAllowedIn(invoiceIssuing) &&
    fiscalAvailability?.software_allowed !== false;

  // „Текуща сметка" в избора: първо масата (отворена, не се закрива, над
  // 0.00), после облакът — грешка, „проверява се", не може (причината на BE).
  const billBlock = fiscalInvoiceBillBlockOf(selectedBill, isSelectedBillClosing);
  const currentBlockedReason = billBlock
    ? t(`invoicingChoice.unavailable.${billBlock}`)
    : fiscalAvailabilityError ??
      (fiscalAvailability && !fiscalAvailability.available
        ? localizedTextOf(fiscalAvailability.message, t("fiscalInvoice.picker.unavailable"))
        : null);
  const currentPendingText =
    !currentBlockedReason && !fiscalAvailability ? t("fiscalInvoice.picker.loading") : null;

  const openCreate = (
    bills: BillHistory[],
    free: boolean,
    deposit: DepositInvoiceSource | null,
  ) => {
    setInvoiceBills(bills);
    setIsFreeInvoice(free);
    setInvoiceDeposit(deposit);
    setIsSelectionOpen(false);
    setIsCreateOpen(true);
  };

  const modals = (
    <>
      <InvoicingChoiceModal
        open={isChoiceOpen}
        onClose={() => setIsChoiceOpen(false)}
        showCurrent={fiscalInvoiceOffered}
        showFree={canFreeInvoice}
        current={
          selectedBill
            ? {
                tableName: selectedBill.name,
                amount: (parseFloat(selectedBill.total_amount) || 0).toFixed(2),
              }
            : null
        }
        printerName={fiscalAvailability?.printer_name ?? null}
        currentBlockedReason={currentBlockedReason}
        currentPendingText={currentPendingText}
        softwareBlockedReason={softwareAllowed ? null : t("fiscalInvoice.mode.softwareOff")}
        closedBlockedReason={
          invoiceIssuing === "fiscal" ? t("fiscalInvoice.mode.closedBillInFiscal") : null
        }
        onChoose={(choice) => {
          setIsChoiceOpen(false);
          if (choice === "closed") setIsSelectionOpen(true);
          else if (choice === "free") openCreate([], true, null);
          else if (selectedBill) setFiscalInvoiceBill(selectedBill);
        }}
      />

      <InvoiceBillSelectionModal
        open={isSelectionOpen}
        onClose={() => setIsSelectionOpen(false)}
        onConfirm={(bills) => openCreate(bills, false, null)}
        softwareAllowed={softwareAllowed}
      />

      {/* Монтира се само за избрана сметка — затворен не тегли клиенти и
          не държи формата (всяко отваряне е празно). */}
      {fiscalInvoiceBill && (
        <FiscalInvoiceBuyerModal
          bill={fiscalInvoiceBill}
          nextNumber={fiscalAvailability?.next_number ?? null}
          // Касата кара картов терминал — „Закрий" ще е само в брой
          // (`CloseTableModal` чете същия отговор от кеша).
          cardTerminal={fiscalInvoiceCardTerminalOf(fiscalAvailability)}
          softwareAllowed={softwareAllowed}
          onClose={() => setFiscalInvoiceBill(null)}
          onConfirm={(bill, fiscalInvoice) => {
            setFiscalInvoiceBill(null);
            onFiscalInvoiceBuyer?.(bill, fiscalInvoice);
          }}
        />
      )}

      <InvoiceCreateModal
        open={isCreateOpen}
        bills={invoiceBills}
        free={isFreeInvoice}
        deposit={invoiceDeposit}
        onClose={() => setIsCreateOpen(false)}
        onSaved={(invoice) => {
          setIsCreateOpen(false);
          setInvoicePreview(invoice);
        }}
      />

      <InvoicePreviewModal
        open={invoicePreview !== null}
        invoice={invoicePreview}
        onClose={() => setInvoicePreview(null)}
      />
    </>
  );

  return {
    openInvoicing,
    openDepositInvoice: (deposit: DepositInvoiceSource) => openCreate([], false, deposit),
    modals,
    /** Изборът, купувачът или формата е отворена — за общия сигнал на лентата. */
    isOpen: isChoiceOpen || isSelectionOpen || isCreateOpen || fiscalInvoiceBill !== null,
  };
}
